import dayjs from 'dayjs';
import css from "./InlineCalendar.module.css";
import { useSetUrlQuery, useUrlQuery } from '~/util/UrlUtils';
import { z } from 'zod';
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

const PageQuery = z.object({
  period: z.optional(z.string()),
});

type Data = {
  activeDays: string[];
};

export function InlineCalendar(params: { data: Data; }) {
  const { data } = params;
  const calendar = genCalendar();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const query = PageQuery.parse(
    urlQuery
  );

  const [period, setPeriod] = useState(getTypePeriod(query.period));

  const { startDate, endDate } = getPeriod(query.period);
  const diff = Math.round(endDate.diff(startDate, "days", true));

  const selectedDays: string[] = [];
  for (let i = 0; i < diff; i++) {
    selectedDays.push(dayjs(startDate).add(i, "day").format(`YYYY-MM-DD`));
  }

  const checkForScrollPosition = () => {
    const { current } = listRef;
    if (current) {
      const { scrollLeft, scrollWidth, clientWidth } = current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft !== scrollWidth - clientWidth);
    }
  };

  const debounceCheckForScrollPosition = () => {
    let timerId: undefined | NodeJS.Timeout;
    timerId = setTimeout(checkForScrollPosition, 0);
    return {
      cancel: () => {
        if (timerId !== undefined) {
          clearTimeout(timerId);
        }
      }
    };
  };

  const scrollContainerBy = (distance: number) =>
    listRef.current?.scrollBy({ left: distance, behavior: "smooth" });

  useEffect(() => {
    const { current } = listRef;
    checkForScrollPosition();
    current?.addEventListener("scroll", debounceCheckForScrollPosition);

    return () => {
      current?.removeEventListener("scroll", debounceCheckForScrollPosition);
      debounceCheckForScrollPosition().cancel();
    };
  }, [debounceCheckForScrollPosition]);

  function changePeriod(value: string) {
    if (value !== 'other') {
      setUrlQuery(
        { ...urlQuery, period: value },
        { preventScrollReset: true }
      );
    }
    setPeriod(getTypePeriod(value));
  }


  return <div style={{
    display: "flex",
    flexDirection: "column",
    flexWrap: "nowrap",
    gap: 20
  }}>
    <div style={{ display: 'flex', gap: 10 }}>
      <div className={clsx(css.button, { [css.buttonActive]: period === 'today' })} onClick={() => changePeriod('today')}>Today</div>
      <div className={clsx(css.button, { [css.buttonActive]: period === 'week' })} onClick={() => changePeriod('week')}>Week</div>
      <div className={clsx(css.button, { [css.buttonActive]: period === 'month' })} onClick={() => changePeriod('month')}>Month</div>
      <div className={clsx(css.button, { [css.buttonActive]: period === 'other' })} onClick={() => changePeriod('other')}>Other</div>
    </div>
    {period === 'other' && <div className={css.root}>
      <div className={css.controlLeft}
        onClick={() => scrollContainerBy(-400)}
      >-</div>
      <div ref={listRef} className={css.quarter}>{calendar.map((month, i) => <div key={i} className={css.month}>
        <div>
          <div className={css.monthName}>{dayjs(month[0]).format("MMMM")}</div>
          <div className={css.wrap}>
            {month.map((d, i) => <div key={i} className={css.day} style={selectedDays.includes(d) ? { background: "#a16c0b", color: "#fff", fontWeight: 600 } : {}} onClick={() => changePeriod(`${d}:${d}`)}>{data.activeDays.includes(d) ? <span className={css.activeDay}></span> : null}{dayjs(d).format("D")}</div>)}
          </div>
        </div>
      </div>)
      }</div>
      <div
        className={css.controlRight}
        onClick={() => scrollContainerBy(400)}
      >+</div>
    </div>}
  </div>;
}

type Calendar = string[][];

function genCalendar() {
  const curDay = dayjs().startOf("day");

  const months = [];
  for (let i = 0; i <= 2; i++) {
    months.push(curDay.subtract(i, "month").format("YYYY-MM-DD"));
  }

  const calendar: Calendar = [];
  for (const m of months) {
    const _date = dayjs(m);
    const daysInMonth = _date.daysInMonth();
    let lastDay = _date.isSame(curDay, "month") ? curDay.get("D") : daysInMonth;
    const arrayMonth: string[] = [];

    for (let j = (lastDay - 1); j >= 0; j--) {
      arrayMonth.push(_date.startOf("month").add(j, "day").format("YYYY-MM-DD"));
    }
    calendar.push(arrayMonth);
  }

  return calendar;
}

function getPeriod(value: string | undefined) {
  const todayDate = dayjs();
  const yesterdayDate = todayDate.subtract(1, "day");
  const lastWeek = todayDate.subtract(6, "day");
  const lastMonth = todayDate.subtract(29, "day");

  let period: string | undefined;
  switch (value) {
    case "today":
      period = `${todayDate.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "yesterday":
      period = `${yesterdayDate.startOf("day").format('YYYY-MM-DD')}:${yesterdayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "week":
      period = `${lastWeek.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    case "month":
      period = `${lastMonth.startOf("day").format('YYYY-MM-DD')}:${todayDate.endOf("day").format('YYYY-MM-DD')}`;
      break;
    default:
      period = value;
      break;
  }

  const [strStartDate, strEndDate] = period ? period.split(":", 2) : [];

  const startDate = dayjs(strStartDate).isValid()
    ? dayjs(strStartDate).startOf("day")
    : dayjs().startOf("day");
  const endDate = dayjs(strEndDate).isValid()
    ? dayjs(strEndDate).endOf("day")
    : dayjs().endOf("day");

  return { startDate, endDate };
}

function Picker() {
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'other'>('today');

  return <div style={{ display: 'flex', gap: 10 }}>
    <div className={css.button} onClick={e => setPeriod('today')}>Day</div>
    <div className={css.button} onClick={e => setPeriod('week')}>Week</div>
    <div className={css.button} onClick={e => setPeriod('month')}>Month</div>
    <div className={css.button}>Other</div>
  </div>;
}

function getTypePeriod(period: string | undefined) {
  // let _period: 'today' | 'week' | 'month' | 'other';
  switch (period) {
    case undefined:
    case 'today':
      return 'today';
    case 'week':
      return 'week';
    case 'month':
      return 'month';
    default:
      return 'other';
  }
}